import axios from 'axios';
import { createApiClient } from '../../api/client';
import type { DelegateUploadAck, DelegateUploadRequest } from './types';

/**
 * un-bulk-upload talks to the `bulkupload` REST API
 * (`/api/v1/bulkupload/conferences/delegate-uploads`). Contract source:
 * `flows/un-bulk-upload/API_CONTRACT.md` (authoritative — PRD §7).
 *
 * Auth: standard authenticated embed. The host-injected bearer JWT (carrying
 * the `CAN_SEE_CUSTOM_BULK_UPLOAD` authority + `company_id` / `user_id` claims)
 * travels in the `Authorization` header, attached by `createApiClient` — never
 * in the body. The conference identity is derived server-side from the token +
 * lum-iam, so the request carries only `{ file, executePayments }` (PRD §4).
 *
 * Base URL is `VITE_API_URL` from `.env.<mode>` — empty in dev / unit tests so
 * MSW (relative-path handlers) intercepts; per-env builds point it at the real
 * service.
 */
const API_BASE = import.meta.env.VITE_API_URL ?? '';
export const DELEGATE_UPLOADS_PATH = '/api/v1/bulkupload/conferences/delegate-uploads';

const apiClient = () => createApiClient(API_BASE);

// A 2xx with non-numeric counts would otherwise paint a confident "NaN total
// records" summary, so reject it as contract drift (mirrors un-custom-dashboard).
const isUploadAck = (d: unknown): d is DelegateUploadAck => {
  const a = d as Partial<DelegateUploadAck> | null;
  return (
    !!a &&
    Number.isFinite(a.totalLinesCount) &&
    Number.isFinite(a.validLinesCount) &&
    Number.isFinite(a.wrongValidationCount)
  );
};

/**
 * Thrown when a 2xx response doesn't match the contract. Carries an `errorCode`
 * so `parseApiError` can classify it distinctly from a network error (otherwise
 * contract drift is invisible in telemetry — it looks like an offline failure).
 */
export class ContractDriftError extends Error {
  readonly errorCode = 'MALFORMED_RESPONSE';
  constructor(message: string) {
    super(message);
    this.name = 'ContractDriftError';
  }
}

type NoValidLinesBody = {
  errorCode?: string;
  totalLinesCount?: number;
  wrongValidationCount?: number;
  errorFile?: string;
};

/**
 * The backend returns `400 NO_VALID_LINES` (not a 2xx) when every parseable row
 * is invalid — but the body still carries the same counts + `errorFile` as a
 * success. Surface it as a "0 valid" result so the user sees the summary and can
 * download the error report (e.g. "Duplicated Email"), instead of a dead-end
 * toast. A truly empty/garbage file (`totalLinesCount` 0 / absent) is NOT a
 * result — it stays a normal error (PRD §7 / O-13).
 */
const noValidLinesAck = (error: unknown): DelegateUploadAck | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 400) return null;
  const b = error.response.data as NoValidLinesBody | undefined;
  if (b?.errorCode !== 'NO_VALID_LINES' || !Number.isFinite(b.totalLinesCount) || (b.totalLinesCount ?? 0) <= 0) {
    return null;
  }
  const total = b.totalLinesCount as number;
  return {
    totalLinesCount: total,
    validLinesCount: 0,
    wrongValidationCount: Number.isFinite(b.wrongValidationCount) ? (b.wrongValidationCount as number) : total,
    errorFile: typeof b.errorFile === 'string' ? b.errorFile : undefined,
    accepted: false,
  };
};

/** Upload + synchronously validate the delegate CSV; returns the summary. */
export const uploadDelegates = async (
  body: DelegateUploadRequest,
): Promise<DelegateUploadAck> => {
  let data: DelegateUploadAck;
  try {
    ({ data } = await apiClient().post<DelegateUploadAck>(DELEGATE_UPLOADS_PATH, body));
  } catch (e) {
    const ack = noValidLinesAck(e);
    if (ack) return ack;
    throw e;
  }
  if (!isUploadAck(data)) {
    throw new ContractDriftError('Malformed delegate-upload response');
  }
  return data;
};

// --- Error extraction ------------------------------------------------------

export type ApiError = {
  /** HTTP status (undefined for a transport/network error with no response). */
  status?: number;
  /** `errorCode` from the `{ statusCode, errorCode, message }` envelope (PRD §7). */
  errorCode: string | null;
};

type ErrorBody = { errorCode?: string };

/** Normalise an unknown thrown error into `{ status, errorCode }`. */
export const parseApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as ErrorBody | undefined;
    return { status: error.response?.status, errorCode: body?.errorCode ?? null };
  }
  // Contract drift on a 2xx: surface its errorCode so monitoring can tell it
  // apart from a transport/network error (which has errorCode null).
  if (error instanceof ContractDriftError) {
    return { errorCode: error.errorCode };
  }
  return { errorCode: null };
};
